import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';
import { Package, Download, Eye, Plus, Filter, ChevronDown, ChevronRight, MapPin, Truck, Calendar, RefreshCw, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { authAPI } from '../lib/api';

// Type definitions for API responses
interface Label {
  id: number;
  package_index: number;
  status: string;
  tracking_number?: string;
  label_image_base64?: string;
  label_image_url?: string;
  label_pdf_url?: string;
  created_at: string;
  updated_at: string;
  package: Package; // Add package field to match backend serializer
  individual_to_address?: ShippingAddress; // Individual address for this specific package
}

interface ShippingAddress {
  id: number;
  name: string;
  company?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country_code: string;
  phone?: string;
  email?: string;
}

interface Package {
  id: number;
  weight: number;
  weight_unit: string;
  length: number;
  width: number;
  height: number;
  dimension_unit: string;
}

interface Shipment {
  id: number;
  shipment_tracking_number?: string;
  service_type: string;
  status: string;
  quantity: number;
  shipping_cost?: number;
  display_shipping_cost?: number;
  show_rate?: boolean;
  currency: string;
  created_at: string;
  updated_at: string;
  reference_number?: string;
  description?: string;
  declared_value?: number;
  created_by: string;
  bulk_job?: number;  // Bulk job ID if this shipment is from bulk upload
  from_address: ShippingAddress;
  to_address: ShippingAddress;
  package: Package;
  labels: Label[];
}

interface ShipmentsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Shipment[];
}

const ShipmentsList: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selectedShipments, setSelectedShipments] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedShipments, setExpandedShipments] = useState<number[]>([]);
  const [expandedPackages, setExpandedPackages] = useState<{[shipmentId: number]: number[]}>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [downloadingShipmentId, setDownloadingShipmentId] = useState<number | null>(null);
  const [downloadingBulkJobId, setDownloadingBulkJobId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [showExportDialog, setShowExportDialog] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const shipmentsQuery = useQuery<ShipmentsResponse>({
    queryKey: ['shipments', { status: statusFilter, page: currentPage }, location.pathname],
    queryFn: () => optimizedApiService.shipments.getShipments({ 
      page: currentPage,
      page_size: 20,  // Fixed page size
      ...(statusFilter !== 'all' && { status: statusFilter })
    }) as Promise<ShipmentsResponse>,
  });

  // Check if user is admin
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await authAPI.getUserInfo();
        setIsAdmin(response.data.is_staff || false);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };
    fetchUserInfo();
  }, []);

  // Handle errors
  useEffect(() => {
    if (shipmentsQuery.error) {
      errorHandler.handleApiError(shipmentsQuery.error as any, 'Shipments List');
    }
  }, [shipmentsQuery.error]);

  // Reset pagination when navigating to this page
  useEffect(() => {
    console.log('ShipmentsList: Resetting pagination on navigation');
    setCurrentPage(1);
    setSelectedShipments([]);
    setExpandedShipments([]);
    setExpandedPackages({});
    setDownloadingShipmentId(null);
    setDownloadingBulkJobId(null);
    // Force query refresh
    queryClient.invalidateQueries({ queryKey: ['shipments'] });
  }, [location.pathname, queryClient]);

  // Calculate total pages when data arrives
  useEffect(() => {
    if (shipmentsQuery.data) {
      const { count } = shipmentsQuery.data;
      const calculatedTotalPages = Math.ceil(count / 20);
      
      console.log('ShipmentsList: Calculating total pages', {
        totalCount: count,
        calculatedTotalPages,
        currentPage
      });
      
      setTotalPages(calculatedTotalPages);
    }
  }, [shipmentsQuery.data, shipmentsQuery.data?.count]);

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const { data: shipmentsData, isLoading } = shipmentsQuery;

  // Load more shipments function - goes to next page
  const loadMoreShipments = () => {
    if (currentPage < totalPages && !isLoading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Check if there are more shipments to load
  const hasMoreShipments = currentPage < totalPages;

  const processShipmentMutation = useMutation({
    mutationFn: (shipmentId: number) => optimizedApiService.shipments.processUpsShipment(shipmentId),
    onSuccess: () => {
      toast.success('Shipment processed successfully!');
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['labels'] });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Process Shipment');
    }
  });

  const downloadShipmentPdfMutation = useMutation({
    mutationFn: (shipmentId: number) => {
      setDownloadingShipmentId(shipmentId);
      return optimizedApiService.shipments.downloadShipmentPdf(shipmentId);
    },
    onSuccess: (response: any) => {
      // Handle blob response for PDF download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipment_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Shipment PDF downloaded successfully!');
      setDownloadingShipmentId(null);
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Download Shipment PDF');
      setDownloadingShipmentId(null);
    }
  });

  const downloadBulkLabelsMutation = useMutation({
    mutationFn: (bulkJobId: number) => {
      setDownloadingBulkJobId(bulkJobId);
      return optimizedApiService.bulkJobs.downloadAllLabels(bulkJobId);
    },
    onSuccess: (response: any) => {
      // Handle blob response for PDF download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_labels_${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Bulk labels PDF downloaded successfully!');
      setDownloadingBulkJobId(null);
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Download Bulk Labels');
      setDownloadingBulkJobId(null);
    }
  });

  const exportShipmentsMutation = useMutation({
    mutationFn: ({ dateFrom, dateTo, isAdmin: adminMode }: { dateFrom?: string, dateTo?: string, isAdmin?: boolean }) => {
      if (adminMode) {
        return optimizedApiService.shipments.adminExportShipments(dateFrom, dateTo);
      }
      return optimizedApiService.shipments.exportShipments(dateFrom, dateTo);
    },
    onSuccess: (response: any) => {
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shipments_export_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Shipments exported successfully!');
      setShowExportDialog(false);
      setDateFrom('');
      setDateTo('');
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Export Shipments');
    }
  });

  const handleExport = (adminMode: boolean = false) => {
    exportShipmentsMutation.mutate({ 
      dateFrom: dateFrom || undefined, 
      dateTo: dateTo || undefined,
      isAdmin: adminMode
    });
  };

  const toggleShipmentExpansion = (shipmentId: number) => {
    setExpandedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  const togglePackageExpansion = (shipmentId: number, packageIndex: number) => {
    setExpandedPackages(prev => {
      const currentExpanded = prev[shipmentId] || [];
      const isExpanded = currentExpanded.includes(packageIndex);
      
      return {
        ...prev,
        [shipmentId]: isExpanded 
          ? currentExpanded.filter(index => index !== packageIndex)
          : [...currentExpanded, packageIndex]
      };
    });
  };

  const toggleShipmentSelection = (shipmentId: number) => {
    setSelectedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'created': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'printed': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceTypeName = (serviceType: string) => {
    const services: { [key: string]: string } = {
      '01': 'Next Day Air',
      '02': '2nd Day Air',
      '03': 'Ground',
      '12': '3 Day Select',
      '13': 'Next Day Air Saver',
      '14': 'Next Day Air Early AM',
      '15': 'Express Plus',
      '16': '2nd Day Air AM'
    };
    return services[serviceType] || serviceType;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Use query data directly for display
  const shipments = shipmentsQuery.data?.results || [];
  
  console.log('ShipmentsList: Render', {
    isLoading,
    shipmentsDataCount: shipmentsData?.count || 0,
    shipmentsDataResults: shipmentsData?.results?.length || 0,
    shipmentsLength: shipments.length,
    currentPage,
    totalPages,
    statusFilter,
    hasMoreShipments,
    hasShipmentsData: !!shipmentsData,
    hasResults: !!shipmentsData?.results
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shipments</h1>
            <p className="text-gray-600 mt-2">Manage your shipping shipments and labels</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(!showExportDialog)}
              className="flex items-center space-x-2"
            >
              <FileDown className="h-4 w-4" />
              <span>Export CSV</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['shipments'] });
                queryClient.invalidateQueries({ queryKey: ['labels'] });
                queryClient.invalidateQueries({ queryKey: ['packages'] });
                queryClient.invalidateQueries({ queryKey: ['addresses'] });
                toast.success('Data refreshed!');
              }}
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </Button>
            <Link to="/create-label">
              <Button className="flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Create Shipment</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Export Dialog */}
        {showExportDialog && (
          <Card>
            <CardHeader>
              <CardTitle>Export Shipments</CardTitle>
              <CardDescription>
                Export your shipment data to CSV. {isAdmin && 'As an admin, you can export all users\' data with rate details.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    {dateFrom || dateTo ? (
                      <span>Export shipments from {dateFrom || 'beginning'} to {dateTo || 'today'}</span>
                    ) : (
                      <span>Export all shipments</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowExportDialog(false);
                        setDateFrom('');
                        setDateTo('');
                      }}
                    >
                      Cancel
                    </Button>
                    {isAdmin && (
                      <Button
                        onClick={() => handleExport(true)}
                        loading={exportShipmentsMutation.isPending}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        Admin Export (All Users)
                      </Button>
                    )}
                    <Button
                      onClick={() => handleExport(false)}
                      loading={exportShipmentsMutation.isPending}
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Export My Shipments
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Status Monitor */}
        <ServiceStatusMonitor />

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="created">Created</option>
                <option value="completed">Completed</option>
                <option value="printed">Printed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="error">Error</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Shipments List */}
        <div className="space-y-4">
          {shipments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No shipments found</h3>
                <p className="text-gray-600 mb-4">Create your first shipment to get started</p>
                <Link to="/create-label">
                  <Button>Create Shipment</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            shipments.map((shipment) => (
              <Card key={shipment.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleShipmentExpansion(shipment.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {expandedShipments.includes(shipment.id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      <div>
                        <CardTitle className="text-lg">
                          Shipment #{shipment.id}
                          {shipment.shipment_tracking_number && (
                            <span className="text-sm font-normal text-gray-600 ml-2">
                              ({shipment.shipment_tracking_number})
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription className="flex items-center space-x-4 mt-1">
                          <span className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            <span>{shipment.to_address.city}, {shipment.to_address.state_province}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Truck className="h-3 w-3" />
                            <span>{getServiceTypeName(shipment.service_type)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Package className="h-3 w-3" />
                            <span>{shipment.quantity} package{shipment.quantity > 1 ? 's' : ''}</span>
                          </span>
                          {shipment.show_rate && shipment.display_shipping_cost && (
                            <span className="flex items-center space-x-1 font-semibold text-green-700">
                              <span>💰</span>
                              <span>${shipment.display_shipping_cost.toFixed(2)} {shipment.currency}</span>
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge className={getStatusColor(shipment.status)}>
                        {shipment.status.charAt(0).toUpperCase() + shipment.status.slice(1)}
                      </Badge>
                      <div className="flex items-center space-x-2">
                        {shipment.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => processShipmentMutation.mutate(shipment.id)}
                            loading={processShipmentMutation.isPending}
                          >
                            Process
                          </Button>
                        )}
                        {(shipment.status === 'created' || shipment.status === 'completed') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (shipment.bulk_job) {
                                // For bulk shipments, download all labels from the bulk job
                                downloadBulkLabelsMutation.mutate(shipment.bulk_job);
                              } else {
                                // For individual shipments, download just this shipment's labels
                                downloadShipmentPdfMutation.mutate(shipment.id);
                              }
                            }}
                            loading={
                              (shipment.bulk_job && downloadingBulkJobId === shipment.bulk_job) ||
                              (!shipment.bulk_job && downloadingShipmentId === shipment.id)
                            }
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Labels
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {/* Expanded Labels */}
                {expandedShipments.includes(shipment.id) && (
                  <CardContent className="pt-0">
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Package Labels ({shipment.labels.length})</h4>
                      <div className="space-y-2">
                        {shipment.labels.map((label) => {
                          const isPackageExpanded = expandedPackages[shipment.id]?.includes(label.package_index) || false;
                          
                          return (
                            <div key={label.id} className="bg-gray-50 rounded-lg">
                              {/* Package Header */}
                              <div className="flex items-center justify-between p-3">
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => togglePackageExpansion(shipment.id, label.package_index)}
                                    className="p-1 hover:bg-gray-200 rounded"
                                  >
                                    {isPackageExpanded ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </button>
                                  <Package className="h-4 w-4 text-gray-500" />
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-medium">Package #{label.package_index}</span>
                                      {label.tracking_number && (
                                        <Badge className="bg-blue-100 text-blue-800 text-xs font-mono">
                                          {label.tracking_number}
                                        </Badge>
                                      )}
                                    </div>
                                    {!label.tracking_number && (
                                      <span className="text-xs text-gray-500 italic">No tracking number</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge className={getStatusColor(label.status)}>
                                    {label.status.charAt(0).toUpperCase() + label.status.slice(1)}
                                  </Badge>
                                  {label.label_image_base64 && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const binaryString = atob(label.label_image_base64!);
                                        const bytes = new Uint8Array(binaryString.length);
                                        for (let i = 0; i < binaryString.length; i++) {
                                          bytes[i] = binaryString.charCodeAt(i);
                                        }
                                        const blob = new Blob([bytes], { type: 'image/png' });
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = `package_${label.package_index}_label.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                      }}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {/* Package Details (Expandable) */}
                              {isPackageExpanded && (
                                <div className="px-3 pb-3 border-t border-gray-200">
                                  <div className="pt-3 space-y-3">
                                    {/* Package Information */}
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">Weight:</span>
                                        <span className="ml-2 font-medium">{label.package.weight} {label.package.weight_unit}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Dimensions:</span>
                                        <span className="ml-2 font-medium">
                                          {label.package.length}×{label.package.width}×{label.package.height} {label.package.dimension_unit}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Tracking Information */}
                                    <div className="space-y-2">
                                      <h5 className="text-sm font-medium text-gray-900">Tracking Information</h5>
                                      <div className="space-y-2">
                                        {label.tracking_number ? (
                                          <div className="flex items-center space-x-2">
                                            <span className="text-gray-600 text-sm">Tracking Number:</span>
                                            <span className="font-mono text-sm font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                              {label.tracking_number}
                                            </span>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                navigator.clipboard.writeText(label.tracking_number!);
                                                toast.success('Tracking number copied to clipboard!');
                                              }}
                                              className="text-xs"
                                            >
                                              Copy
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className="text-sm text-gray-500 italic">
                                            No tracking number assigned yet
                                          </div>
                                        )}
                                        {shipment.shipment_tracking_number && (
                                          <div className="flex items-center space-x-2">
                                            <span className="text-gray-600 text-sm">Shipment Tracking:</span>
                                            <span className="font-mono text-sm font-medium bg-green-100 text-green-800 px-2 py-1 rounded">
                                              {shipment.shipment_tracking_number}
                                            </span>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                navigator.clipboard.writeText(shipment.shipment_tracking_number!);
                                                toast.success('Shipment tracking number copied to clipboard!');
                                              }}
                                              className="text-xs"
                                            >
                                              Copy
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Label Information */}
                                    <div className="space-y-2">
                                      <h5 className="text-sm font-medium text-gray-900">Label Information</h5>
                                      <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                          <span className="text-gray-600">Created:</span>
                                          <span className="ml-2 font-medium">
                                            {new Date(label.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Updated:</span>
                                          <span className="ml-2 font-medium">
                                            {new Date(label.updated_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Address Information */}
                                    <div className="space-y-2">
                                      <h5 className="text-sm font-medium text-gray-900">Delivery Address</h5>
                                      <div className="text-sm text-gray-700">
                                        {/* Use individual address if available, otherwise fall back to shipment address */}
                                        {label.individual_to_address ? (
                                          <>
                                            <div className="font-medium">{label.individual_to_address.name}</div>
                                            {label.individual_to_address.company && (
                                              <div>{label.individual_to_address.company}</div>
                                            )}
                                            <div>{label.individual_to_address.address_line_1}</div>
                                            {label.individual_to_address.address_line_2 && (
                                              <div>{label.individual_to_address.address_line_2}</div>
                                            )}
                                            <div>
                                              {label.individual_to_address.city}, {label.individual_to_address.state_province} {label.individual_to_address.postal_code}
                                            </div>
                                            <div>{label.individual_to_address.country_code}</div>
                                            {label.individual_to_address.phone && (
                                              <div className="mt-1">Phone: {label.individual_to_address.phone}</div>
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            <div className="font-medium">{shipment.to_address.name}</div>
                                            {shipment.to_address.company && (
                                              <div>{shipment.to_address.company}</div>
                                            )}
                                            <div>{shipment.to_address.address_line_1}</div>
                                            {shipment.to_address.address_line_2 && (
                                              <div>{shipment.to_address.address_line_2}</div>
                                            )}
                                            <div>
                                              {shipment.to_address.city}, {shipment.to_address.state_province} {shipment.to_address.postal_code}
                                            </div>
                                            <div>{shipment.to_address.country_code}</div>
                                            {shipment.to_address.phone && (
                                              <div className="mt-1">Phone: {shipment.to_address.phone}</div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                                      {label.label_image_base64 && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            const binaryString = atob(label.label_image_base64!);
                                        const bytes = new Uint8Array(binaryString.length);
                                        for (let i = 0; i < binaryString.length; i++) {
                                          bytes[i] = binaryString.charCodeAt(i);
                                        }
                                        const blob = new Blob([bytes], { type: 'image/png' });
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `package_${label.package_index}_label.png`;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                            window.URL.revokeObjectURL(url);
                                          }}
                                        >
                                          <Download className="h-4 w-4 mr-1" />
                                          Download Label
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          // Open label image in new tab
                                          const binaryString = atob(label.label_image_base64!);
                                        const bytes = new Uint8Array(binaryString.length);
                                        for (let i = 0; i < binaryString.length; i++) {
                                          bytes[i] = binaryString.charCodeAt(i);
                                        }
                                        const blob = new Blob([bytes], { type: 'image/png' });
                                          const url = window.URL.createObjectURL(blob);
                                          window.open(url, '_blank');
                                        }}
                                        disabled={!label.label_image_base64}
                                      >
                                        <Eye className="h-4 w-4 mr-1" />
                                        View Label
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && shipments.length > 0 && (
            <div className="flex justify-center items-center space-x-4 mt-6">
              {/* Previous Button */}
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
                variant="outline"
                className="px-4 py-2"
              >
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={isLoading}
                      variant={currentPage === pageNum ? "primary" : "outline"}
                      className="px-3 py-2 min-w-[40px]"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              {/* Next Button */}
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || isLoading}
                variant="outline"
                className="px-4 py-2"
              >
                Next
              </Button>
            </div>
          )}
          
          {/* Load More Button (Alternative) */}
          {hasMoreShipments && shipments.length > 0 && (
            <div className="flex justify-center mt-4">
              <Button
                onClick={loadMoreShipments}
                disabled={isLoading}
                variant="outline"
                className="px-8 py-2"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Load More Shipments
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Pagination Info */}
          {shipmentsData && (
            <div className="text-center text-sm text-gray-600 mt-4">
              Showing {shipments.length} of {shipmentsData.count} shipments
              {totalPages > 1 && (
                <span className="ml-2">
                  (Page {currentPage} of {totalPages})
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipmentsList;
